import React, { useMemo, useState } from "react";
import withClinic from "../../hooks/with_clinic";
import { ApolloResult, Clinic } from "../../store";
import { Row, Col, Typography, Empty, Avatar } from "antd";
import Search from "antd/lib/input/Search";
import DateRange from "../../components/date_range";
import { Form } from "antd";
import { Table } from "antd";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useApolloClient, useQuery } from "@apollo/client";
import LoadingSpinner from "../../components/loading_indicator";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import Error from "../../components/apollo_error";
import {
  getGroupByCheckInPractitionerCountVar,
  get_group_by_checkin_practitioner_count,
} from "../../graphql/practitioner";
import { ExportExcel } from "../../helpers/excel";
import { FileExcelFilled } from "@ant-design/icons";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import paths from "../../routes/paths";
import { PaginationConfig } from "antd/lib/pagination";

interface P {
  currentClinic?: Clinic;
}

interface TopPractitionerRow {
  id: string;
  name: string;
  image?: string | null;
  check_in: number;
}

type GroupByCheckInPractitionerCountRow = {
  _count: { _all: number };
  practitioner_id: string;
  practitioner_name: string;
  practitioner_image?: string | null;
};

type CheckInPractitionerCountResponse = {
  data: GroupByCheckInPractitionerCountRow[];
  totalCount: number;
};

const TopPractitionerReport = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const client = useApolloClient();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });
  const [pagination, setPagination] = useState<PaginationConfig | null>({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
  });
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;

  const variables = getGroupByCheckInPractitionerCountVar({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    skip,
    take,
  });

  const { loading, data, error } = useQuery<
    ApolloResult<
      "groupByCheckInPractitionerCount",
      CheckInPractitionerCountResponse
    >
  >(get_group_by_checkin_practitioner_count, {
    variables,
    skip: !clinicId,
    fetchPolicy: "cache-and-network",
  });

  const practitioners = useMemo(() => {
    const rows = (
      data?.groupByCheckInPractitionerCount?.data || []
    ).map((row) => ({
      id: row.practitioner_id,
      name: row.practitioner_name,
      image: row.practitioner_image,
      check_in: row._count._all,
    }));

    if (!searchText) return rows;

    const query = searchText.toLowerCase();
    return rows.filter((row) => row.name.toLowerCase().includes(query));
  }, [data, searchText]);

  const totalCount = searchText
    ? practitioners.length
    : data?.groupByCheckInPractitionerCount?.totalCount ?? 0;

  const handleExport = async () => {
    const fileName =
      "top_practitioner" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    setExporting(true);

    try {
      const exportResult = await client.query<
        ApolloResult<
          "groupByCheckInPractitionerCount",
          CheckInPractitionerCountResponse
        >
      >({
        query: get_group_by_checkin_practitioner_count,
        variables: getGroupByCheckInPractitionerCountVar({
          clinicId,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
          skip: 0,
          take: 100000,
        }),
        fetchPolicy: "network-only",
      });

      let rows = (
        exportResult.data?.groupByCheckInPractitionerCount?.data || []
      ).map((row) => ({
        Name: row.practitioner_name,
        CheckOut: row._count._all,
      }));

      if (searchText) {
        const query = searchText.toLowerCase();
        rows = rows.filter((row) => row.Name.toLowerCase().includes(query));
      }

      const totalCheckInCount = rows.reduce(
        (total, row) => total + row.CheckOut,
        0
      );
      rows.push({
        Name: "Total",
        CheckOut: totalCheckInCount,
      });

      ExportExcel(rows, fileName);
    } finally {
      setExporting(false);
    }
  };

  const practitionerOnClick = (practitioner: TopPractitionerRow) => {
    navigate(paths.getPractitionerTreatmentReport(practitioner.id), {
      state: { ranges },
    });
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns: any[] = [
    {
      title: "Photo",
      dataIndex: "image",
      key: "image",
      render: (_v: string, record: TopPractitionerRow) => (
        <Avatar src={record.image} size={50} />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      editable: false,
      sorter: (a: TopPractitionerRow, b: TopPractitionerRow) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      render: (_text: string, record: TopPractitionerRow) => record.name,
    },
    {
      title: "CheckIn",
      dataIndex: "check_in",
      key: "check_in",
      sorter: (a: TopPractitionerRow, b: TopPractitionerRow) =>
        a.check_in - b.check_in,
      sortOrder: "descend",
      render: (_text: number, record: TopPractitionerRow) => record.check_in,
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography level={2}>Top Practitioner Report</Typography>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${
                props.currentClinic?._count.practitioners || 0
              } practitioners...`}
              allowClear
              size="middle"
              onSearch={(val: string) => {
                setSearchText(val);
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
      <Col span={2}>
        <Button
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              loading={loading}
              showHeader={practitioners.length > 0}
              dataSource={practitioners.map((b) => ({ key: b.id, ...b }))}
              columns={columns}
              rowClassName="clickable-row"
              scroll={{ x: 800 }}
              pagination={{
                total: totalCount,
                current: pagination?.current || 1,
                pageSize: pagination?.pageSize || 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                onChange: (page: number, pageSize: number) => {
                  setPagination({
                    ...pagination,
                    current: page,
                    pageSize,
                  });
                },
              }}
              onRow={(record: TopPractitionerRow) => {
                return {
                  onClick: () => {
                    practitionerOnClick(record);
                  },
                };
              }}
            />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {context}
    </React.Fragment>
  );
};

export default withClinic(TopPractitionerReport);
